import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  InjectionToken,
  OnInit,
  ViewEncapsulation,
  computed,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule, UntypedFormGroup } from '@angular/forms';
import { CampaignAddressSearchField, CampaignField } from '@fmnts/api/next';
import {
  FindResponse,
  LoqateAddressCaptureApi,
  LoqateResponseType,
  RetrieveResponse,
} from '@fmnts/api/vendors/loqate';
import { ConnectionService } from '@fmnts/common';
import { FmntsFormFieldModule } from '@fmnts/components/form-field';
import { FmntsProgressIndicatorModule } from '@fmnts/components/progress-indicator';
import { Maybe, isNonEmptyString, isString, optionHasValue } from '@fmnts/core';
import { includedIn, stringMatching, weighBy } from '@fmnts/core/collection';
import {
  AbstractDeclarativeInputFieldComponent,
  ErrorMessagePipe,
  isFieldDeclaration,
  isFormSelectField,
} from '@fmnts/declarative-forms';
import { DeclarativeGeolocationService } from '@fmnts/declarative-forms/geolocation';
import { I18nModule } from '@fmnts/i18n';
import { FormFieldService } from '@formunauts/shared/campaigning/data-access';
import { GoogleMapsService } from '@formunauts/shared/vendors/google-maps/data-access';
import {
  getState,
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import * as Boolean from 'effect/Boolean';
import * as O from 'effect/Option';
import * as String from 'effect/String';
import {
  EMPTY,
  Observable,
  Subject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  merge,
  of,
  partition,
  pipe,
  repeat,
  skip,
  skipWhile,
  switchMap,
  take,
  takeUntil,
  tap,
  timeout,
} from 'rxjs';
import { mapLoqateAddressProperty } from './address-search-field.util';

type FieldValue = string | null;

interface PrefillMapping {
  [fieldKey: string]: string[];
}

export enum AddressDataState {
  Init = 'init',
  Loading = 'loading',
  Loaded = 'loaded',
}

/**
 * Array with all the types returned by the google address search
 * for which the `short_name` property should be used,
 * instead of `long_name`
 */
const useShortNameComponentTypes = ['country'];

interface AddressSearchFieldState {
  searchTerm: string;
  hasFocus: boolean;
  hasResults: boolean;
  state: AddressDataState;
  googleResults: google.maps.places.AutocompleteSuggestion[];
  results: FindResponse[];
  resultsType: LoqateResponseType | null;
  /** Index of the currently highlighted result. -1 means "none". */
  activeIndex: number;
  provider?: string;
}
const AddressSearchFieldStore = signalStore(
  withState<AddressSearchFieldState>({
    searchTerm: '',
    hasFocus: false,
    hasResults: false,
    state: AddressDataState.Init,
    googleResults: [],
    results: [],
    resultsType: null,
    activeIndex: -1,
    provider: undefined,
  }),
  withComputed((store) => ({
    combinedResults: computed(() => {
      if (!store.provider) {
        return [];
      }
      switch (store.provider()) {
        case 'loqate':
          return store.results();
        case 'google':
          return store.googleResults();
        default:
          return [];
      }
    }),
  })),
  withMethods((store) => ({
    setProvider(provider: string) {
      patchState(store, { provider });
    },
    setSearchTerm: rxMethod<string>(
      pipe(
        tap<string>((searchTerm) =>
          patchState(store, { searchTerm, hasResults: false, activeIndex: -1 }),
        ),
      ),
    ),
    setResults: rxMethod<FindResponse[]>(
      pipe(
        tap<FindResponse[]>((results) =>
          patchState(store, {
            results,
            state: AddressDataState.Loaded,
            hasResults: true,
            activeIndex: results.length > 0 ? 0 : -1,
            resultsType: (results[0]?.Type as LoqateResponseType) || null,
          }),
        ),
      ),
    ),
    setGoogleResults: rxMethod<google.maps.places.AutocompleteSuggestion[]>(
      pipe(
        tap<google.maps.places.AutocompleteSuggestion[]>((results) =>
          patchState(store, {
            googleResults: results,
            state: AddressDataState.Loaded,
            hasResults: true,
            activeIndex: results.length > 0 ? 0 : -1,
          }),
        ),
      ),
    ),
    setState: rxMethod<AddressDataState>(
      pipe(tap<AddressDataState>((state) => patchState(store, { state }))),
    ),
    setActiveIndex(activeIndex: number) {
      patchState(store, { activeIndex });
    },
    incrementIndex() {
      const { activeIndex, results } = getState(store);
      patchState(store, {
        activeIndex: (activeIndex + 1) % store.combinedResults().length,
      });
    },
    decrementIndex() {
      const { activeIndex, results } = getState(store);

      patchState(store, {
        activeIndex: (activeIndex - 1) % store.combinedResults().length,
      });
    },
    focus() {
      patchState(store, { hasFocus: true });
    },
    blur() {
      patchState(store, { hasFocus: false, activeIndex: -1 });
    },
  })),
);

interface AddressSearchConfig {
  debounce: number;
}
export const AddressSearchConfigToken = new InjectionToken<AddressSearchConfig>(
  '@formunauts/next/forms/address-search-field/config',
  {
    factory: () => ({
      debounce: 300,
    }),
  },
);

@Component({
  selector: 'declarative-forms-address-search-field',
  templateUrl: './address-search-field.component.html',
  styleUrl: './address-search-field.component.scss',
  imports: [
    CommonModule,
    I18nModule,
    FmntsFormFieldModule,
    FormsModule,
    ErrorMessagePipe,
    FmntsProgressIndicatorModule,
  ],
  providers: [AddressSearchFieldStore],
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'form-field form-field__text',
    '(keydown)': 'onKeyDown($event)',
  },
})
export class AddressSearchFieldComponent
  extends AbstractDeclarativeInputFieldComponent<
    CampaignAddressSearchField,
    FieldValue
  >
  implements OnInit
{
  private readonly loqateService = inject(LoqateAddressCaptureApi);
  private readonly connectionService = inject(ConnectionService);
  private readonly formHelper = inject(FormFieldService);
  private readonly googleMapsService = inject(GoogleMapsService);
  private readonly geolocation = inject(DeclarativeGeolocationService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly vm = inject(AddressSearchFieldStore);
  protected readonly config = inject(AddressSearchConfigToken);

  /**
   * Value for the `autocomplete` attribute of the input field.
   */
  readonly autocomplete = input(this._getAutocompleteBehavior());

  readonly element =
    viewChild.required<ElementRef<HTMLInputElement>>('element');

  readonly dropdown =
    viewChild.required<ElementRef<HTMLLabelElement>>('dropdown');

  /** `true` if users can improve search result accuracy. */
  protected readonly canImproveSearchAccuracy =
    this.geolocation.canImproveAccuracy;

  /** Observable that emits with the found results */
  public results$: Observable<FindResponse[]> = toObservable(this.vm.results);
  public googleResults$ = toObservable(this.vm.googleResults);

  // Observable that will emit with an array of results
  // If nothing was entered, it will emit an empty array
  private readonly searchValue$: Observable<{ text: string }> = toObservable(
    this.vm.searchTerm,
  ).pipe(
    map(String.trim),
    debounceTime(this.config.debounce),
    map((text) => ({ text })),
  );

  private readonly hasFocus$ = toObservable(this.vm.hasFocus);

  readonly #hasResults$ = combineLatest([
    this.results$,
    toObservable(this.vm.googleResults),
  ]).pipe(
    map(
      ([results, googleResults]) =>
        results.length > 0 || googleResults.length > 0,
    ),
  );

  /** Observable that emits `true`, if the dropdown should be visible */
  protected readonly dropdownShown$: Observable<boolean> = combineLatest([
    this.hasFocus$,
    this.searchValue$,
    this.#hasResults$,
    toObservable(this.vm.state),
  ]).pipe(
    map(
      ([hasFocus, searchTerm, hasResults, state]) =>
        (hasFocus && searchTerm.text.length > 0 && hasResults) ||
        (hasFocus && state === AddressDataState.Loading),
    ),
  );

  /** Emits when the user selects a search result */
  protected readonly selectedSearchResult$ = new Subject<FindResponse>();

  private _value: FieldValue = '';

  ngOnInit(): void {
    this.vm.setProvider(this.provider);

    switch (this.provider) {
      case 'google':
        this.initializeGoogleSearch();
        break;
      case 'loqate':
        this.initializeUKSearch();
        break;
    }
  }

  get value(): FieldValue {
    return this._value;
  }

  set value(value: unknown) {
    this._value = value as FieldValue;
    this.vm.setSearchTerm((value as FieldValue) ?? '');
    this._onChange(value as FieldValue);
    this._onTouched();
  }

  protected handleLoqateKeyDown(event: KeyboardEvent): void {
    const results = this.vm.results();
    if (results.length === 0) {
      // Nothing to navigate/select.
      return;
    }

    switch (event.key) {
      case 'Enter': {
        // Prevent form submission.
        event.preventDefault();
        this.#selectActiveResult(results);
        return;
      }
      case 'Tab': {
        // Ignore the first "Tab" focus handling, while
        // the results just contain containers
        if (this.vm.resultsType() === 'Container') {
          event.preventDefault();
        }

        // when the address was already selected and index was reset
        // do not change the address
        if (this.vm.activeIndex() === -1) {
          return;
        }
        // Select highlighted result
        this.#selectActiveResult(results);
        return;
      }
    }
  }

  protected handleGoogleKeyDown(event: KeyboardEvent): void {
    const results = this.vm.googleResults();
    if (results.length === 0) {
      // Nothing to navigate/select.
      return;
    }

    switch (event.key) {
      case 'Enter': {
        // Prevent form submission.
        event.preventDefault();
        const index = this.vm.activeIndex;
        this.handleSelect(results[index()]);
        return;
      }
      case 'Tab': {
        // when the address was already selected and index was reset
        // do not change the address
        if (this.vm.activeIndex() === -1) {
          return;
        }
        // Select highlighted result
        const index = this.vm.activeIndex;
        this.handleSelect(results[index()]);
        return;
      }
    }
  }

  protected onKeyDown(event: KeyboardEvent): void {
    switch (this.provider) {
      case 'loqate':
        this.handleLoqateKeyDown(event);
        break;
      case 'google':
        this.handleGoogleKeyDown(event);
        break;
    }

    const results = this.vm.combinedResults();
    if (results.length === 0) {
      // Nothing to navigate/select.
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
      case 'Down': {
        event.preventDefault();
        this.vm.incrementIndex();
        return;
      }
      case 'ArrowUp':
      case 'Up': {
        event.preventDefault();
        this.vm.decrementIndex();
        return;
      }
      case 'Escape': {
        event.preventDefault();
        this.closeDropdown();
        return;
      }
    }
  }

  protected onBlur(): void {
    this._onTouched();
  }

  protected openDropdown(): void {
    this.vm.focus();
    const results = this.vm.combinedResults();
    if (results.length > 0 && this.vm.activeIndex() < 0) {
      this.vm.setActiveIndex(0);
    }
  }

  protected selectDropdownOption(
    event: MouseEvent,
    item: FindResponse,
    index: number,
  ): void {
    this.vm.setActiveIndex(index);
    this.selectedSearchResult$.next(item);
  }

  private closeDropdown(): void {
    this.vm.blur();
  }

  protected _onFocus(): void {
    this.openDropdown();
    if (this.provider === 'loqate') {
      this.geolocation.updatePosition();
    }
  }

  @HostListener('document:click', ['$event'])
  public clickOutsideHandler(event: MouseEvent): void {
    if (!this.dropdown().nativeElement.contains(event.target as Node)) {
      this.closeDropdown();
    }
  }

  writeValue(value: unknown): void {
    const newValue = isString(value) ? value : null;
    this._value = newValue;

    // There are different reasons why the value changed, which may include
    // - reset of the form
    // - result written by a selected search result
    const newValueAsString = newValue ?? '';
    if (newValueAsString.toLowerCase() !== this.vm.searchTerm().toLowerCase()) {
      this.vm.setSearchTerm(newValueAsString);
    }
  }

  protected override setFocusedState(isFocused: boolean): void {
    const inputElement = this.element().nativeElement;

    if (isFocused) {
      inputElement.focus();
      this.vm.focus();
    } else {
      inputElement.blur();
      this.vm.blur();
    }
  }

  #selectActiveResult(results: FindResponse[]): void {
    const idx = this.vm.activeIndex();
    const item = results[idx] ?? results[0];
    if (item) {
      this.selectedSearchResult$.next(item);
    }
  }

  get provider(): string {
    return this.field.widgetOptions.provider;
  }

  get autocapitalize(): string | undefined {
    return this.field.widgetOptions.autocapitalize;
  }

  get showError(): boolean {
    return this.control.touched && this.control.invalid;
  }

  private get mapping() {
    return this.field.widgetOptions.mapping || {};
  }

  private async initializeGoogleSearch() {
    // Import the new libraries
    const { AutocompleteSuggestion, AutocompleteSessionToken } =
      await this.googleMapsService.getLibrary();
    let sessionToken = new AutocompleteSessionToken();

    this.googleMapsService.stable$.pipe(take(1)).subscribe((api) => {
      if (!api || google === undefined) {
        throw new Error('Google Maps API was not loaded correctly');
      }

      const [focused$, blurred$] = partition(
        this.hasFocus$.pipe(skip(1)),
        Boolean.eqv(true),
      );
      this.searchValue$
        .pipe(
          filter((s) => isNonEmptyString(s.text)),
          distinctUntilChanged(),
          takeUntil(blurred$),
          tap(async (value) => {
            const request = {
              input: value.text,
              sessionToken: sessionToken,
            };

            const { suggestions } =
              await AutocompleteSuggestion.fetchAutocompleteSuggestions(
                request,
              );
            this.vm.setGoogleResults(suggestions);
          }),
          repeat({ delay: () => focused$ }),
        )
        .subscribe();
    });
  }

  async handleSelect(suggestion: google.maps.places.AutocompleteSuggestion) {
    const place = suggestion.placePrediction?.toPlace();
    if (!place) {
      throw new Error('should have place');
    }

    await place.fetchFields({
      fields: ['addressComponents'],
    });

    const components = place.addressComponents;
    if (!components) {
      return;
    }

    this.closeDropdown();

    // TODO: Re-use mapping from GoogleMaps service
    const result = this.mapGoogleComponentsToFieldValues(components);
    if (Object.keys(result).length === 0) {
      return;
    }

    this._patchFormWithValues(result);
  }

  /**
   * @param components The components from the google place result
   * @param mapping The mapping of field names to google place types
   *
   * @returns
   * Mapping of google address component values to field keys
   */
  private mapGoogleComponentsToFieldValues(
    components: google.maps.places.AddressComponent[],
    mapping: PrefillMapping = this.mapping,
  ) {
    const getValue = (componentTypes: string[]): O.Option<string> => {
      const component = components.find((c) =>
        c.types.some(includedIn(componentTypes)),
      );

      // There is no component with the any of the given component types
      if (!component) {
        return O.none();
      }

      // Use `short_name` if the component_types include any of the special types
      return componentTypes.some(includedIn(useShortNameComponentTypes))
        ? O.some(component.shortText?.toLowerCase() || '')
        : O.some(component.longText || '');
    };

    return this.mapResultToFormValue(mapping, getValue);
  }

  private mapResultToFormValue(
    mapping: PrefillMapping,
    getValue: (types: string[]) => O.Option<string>,
  ): Record<string, unknown> {
    return Object.entries(mapping)
      .map(
        ([fieldKey, componentTypes]) =>
          [fieldKey, getValue(componentTypes)] as const,
      )
      .reduce(
        (result, [fieldKey, value]) => {
          const otherField = this.declaration.get([
            this.fieldset.key,
            fieldKey,
          ]);
          if (otherField && isFieldDeclaration(otherField)) {
            result[fieldKey] = this._getSafeValueForField(
              otherField as CampaignField,
              value,
              result,
            );
          }

          return result;
        },
        {} as Record<string, unknown>,
      );
  }

  /**
   * Initialize Loqate Search for UK Postcodes
   * Creates Mapping which is used for UK
   */
  private initializeUKSearch(): void {
    const [focused$, blurred$] = partition(
      this.hasFocus$.pipe(skip(1)),
      Boolean.eqv(true),
    );

    // We split the selected search result based on its type
    const [retrieve$, detailed$] = partition(
      this.selectedSearchResult$,
      (v) => v.Type === 'Address',
    );

    // Skip emits when results are still cached within the store
    const searchValue$ = this.searchValue$.pipe(
      skipWhile(
        (search) => this.vm.hasResults() || String.isEmpty(search.text),
      ),
    );

    // Observable that emits with a detailed list of results,
    // if a non-address search result was selected
    const detailedResults$ = detailed$.pipe(
      map((container) => ({ container })),
    );

    merge(searchValue$, detailedResults$)
      .pipe(
        tap((query) => {
          this.vm.setState(AddressDataState.Loading);
        }),
        switchMap((query) => this.findAddress(query)),
        tap((r) => {
          this.vm.setResults(r);
        }),
        takeUntil(blurred$),
        repeat({ delay: () => focused$ }),
      )
      .subscribe();

    // Everytime we receive the result from a retrieve request
    // we gonna patch the form values
    retrieve$
      .pipe(
        tap(() => this.vm.setState(AddressDataState.Loading)),
        switchMap((selectedResult) => this.retrieveAddress(selectedResult.Id)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        this.vm.setState(AddressDataState.Loaded);
        const result = this.mapResultToFormValue(
          this.mapping,
          mapLoqateAddressProperty(response),
        );
        // Map values inside the address fieldset
        this._patchFormWithValues(result);
        this.closeDropdown();
      });
  }

  /**
   * Find Address
   * For UK-Postcodes the Find request has to be called 2,
   * first to search for possible Postcodes, the 2nd to get addresses for it
   *
   * @param text
   * @param response
   */
  public findAddress(
    query: { text: string } | { container: FindResponse },
  ): Observable<FindResponse[]> {
    if (!this.connectionService.isOnline) {
      return EMPTY;
    }

    if ('container' in query) {
      return this.loqateService.find({
        container: query.container.Id,
        countries: this.availableCountries(),
      });
    }

    if (query.text.length === 0) {
      return of([]);
    }

    const coordinates$ = this.geolocation.latestCoordinates$.pipe(
      timeout({
        each: 500,
        with: () => of(undefined),
      }),
    );

    return combineLatest([
      coordinates$.pipe(take(1)),
      of(this._currentCountry()),
    ]).pipe(
      switchMap(([coordinates, currentCountry]) =>
        this.loqateService.find({
          text: query.text,
          countries: this.availableCountries(),
          origin: coordinates ?? currentCountry,
          bias: optionHasValue(coordinates),
        }),
      ),
    );
  }

  /**
   * Retrieve Address
   * Send the ID of the Find-Response, to retrieve the complete address
   *
   * @param id returned address of the Find-Request
   */
  public retrieveAddress(id: string): Observable<RetrieveResponse> {
    return this.connectionService.isOnline
      ? this.loqateService.retrieve({ id })
      : EMPTY;
  }

  /**
   * Patches the controls form group parent with the given `values`.
   *
   * @param values Values to write to the parent form group
   * @param opts Additional options
   */
  private _patchFormWithValues(
    values: Record<string, unknown>,
    opts: {
      /** Whether the fields that are written should be marked as dirty */
      markAsDirty?: boolean;
    } = {},
  ): void {
    const form = this.control.parent as UntypedFormGroup;

    const { markAsDirty = true } = opts;
    if (markAsDirty) {
      Object.keys(values).forEach((controlKey) => {
        form.get(controlKey)?.markAsDirty();
      });
    }

    form.patchValue(values);
  }

  /**
   * Countries which should be used for the search
   * @returns array of countries
   */
  private availableCountries(): string[] {
    const country = this.declaration.findByKey('country', isFormSelectField);

    if (!country) {
      return [];
    }
    return [
      country.widgetOptions.defaultValue,
      ...country.widgetOptions.items
        .map(({ value }) => value)
        .filter((c) => c !== country?.widgetOptions.defaultValue),
    ];
  }

  /**
   * Countries which should be used for the search
   * @returns array of countries
   */
  private _currentCountry(): Maybe<string> {
    const country = this.control.parent?.get('country')?.value;

    return isString(country) ? country : undefined;
  }

  /**
   * @param field
   * @param value Value of the address search. Might be falsy
   *
   * @returns
   * The value that is safe to be used for the given `field`.
   * This comes into effect if the given `field` has value restrictions,
   * e.g. for select fields that have a predefined set of allowed items.
   * In that case, the closest matching item is used. If no value matches,
   * `undefined` is returned.
   *
   * For fields without value restrictions, the passed `value` is returned
   */
  private _getSafeValueForField(
    field: CampaignField,
    value: O.Option<string>,
    result: Record<string, unknown>,
  ): unknown | undefined {
    // Overwrite results of the address-search to formValues,
    // so the new values can be used to check conditions
    const updatedFormVaues = {
      ...this.form.flatValue,
      ...result,
    };

    const allowedValues = this.formHelper.getAllowedValues(
      field,
      updatedFormVaues,
    );

    // No value restrictions, return value
    if (!Array.isArray(allowedValues)) {
      return O.getOrUndefined(value);
    }

    // Return undefined when there is nothing to match against
    if (allowedValues.length === 0 || !isString(O.getOrUndefined(value))) {
      return undefined;
    }

    // Compare value against allowed values
    const weighByMatch = weighBy(stringMatching(O.getOrThrow(value)));
    const [first] = weighByMatch(allowedValues as string[]);
    // return the highest match with a weight above 0
    return first.weight > 0 ? first.value : undefined;
  }
}
